import { Application, FeathersService, Paginated, Query, Service } from "@feathersjs/feathers";
import { computed, makeObservable, observable, reaction, runInAction, when } from "mobx";
import { computedFn } from "mobx-utils";
import client from "src/feathers";
import { Tag } from "src/models/tag";
import { sortAlph } from "src/util/util";
import { BaseDataStore, RootStore } from ".";
import { DataType, ITag, ITagItem, ITagSpace, Services } from "../types";
import { DataContainer } from "./dataContainer";


export class TagStore extends BaseDataStore<Tag, ITag> {
  private readonly item_link_service:  FeathersService<Application<ITagItem>, Service<ITagItem>>   = client.service(Services.TagItem);
  private readonly space_link_service: FeathersService<Application<ITagSpace>, Service<ITagSpace>> = client.service(Services.TagSpace);

  activeTags:       number[] = []; // Tags linked to active Item
  activeTagLinkIds: number[] = []; // Id of link in tag-item table for quicker deletes
  roomLinks: DataContainer<ITagItem,  ITagItem> = null;

  constructor(readonly rootStore: RootStore) {
    super(rootStore, Tag.create, DataType.Tag, true);

    makeObservable(this, {
      activeTags:       observable,
      activeTagLinkIds: observable,

      getRoomTags:       computed,
      getActiveItemTags: computed,
      getTagOptions:     computed,
    });

    // Tag - Spaces link event listeners
    this.space_link_service.on("created", (data: ITagSpace) => {
      if(!this.container[Number(data.tag_id)]) return;

      this.updateContainerEntry(
        Number(data.tag_id), "spaces", 
        this.container[Number(data.tag_id)].spaces.concat(Number(data.space_id))
      );
    });
    this.space_link_service.on("removed", (data: ITagSpace) => {
      if(!this.container[Number(data.tag_id)]) return;
      this.updateContainerEntry(
        Number(data.tag_id), "spaces", 
        this.container[Number(data.tag_id)].spaces.filter(t => t !== Number(data.space_id))
      );
    });
  }

  /*******************************************************************************************************************************************
   * Database Access
   ********************************************************************************************************************************************/
  protected loadActiveLinks = async (): Promise<void> => {
    if(this.rootStore.getActiveItem && this.rootStore.getActiveItem.id) {
      const itemLinks: Paginated<ITagItem> = await this.item_link_service.find({
        query: { item_id: this.rootStore.getActiveItem.id}
      }) as unknown as Paginated<ITagItem>;
      runInAction(() => {
        this.activeTags       = itemLinks.total ? itemLinks.data.map(l => Number(l.tag_id)) : [];
        this.activeTagLinkIds = itemLinks.total ? itemLinks.data.map(l => Number(l.id)) : [];
      });
    } else {
      runInAction(() => {
        this.activeTags = [];
        this.activeTagLinkIds = [];
      });
    }
  }
  /*******************************************************************************************************************************************
   * Overwrites
   ********************************************************************************************************************************************/
  init() {
    super.init();
    // Load initial data on login:
    when(() => this.rootStore.authStore.isLoggedIn, () => {
      this.loadItems(this.getCurFilter());
    }, {name: "tag_login_load"});

    // Setup reactions
    reaction(() => this.rootStore.authStore.getCurWorkspaceID, () => {
      if(this.rootStore.authStore.getCurWorkspaceID) this.loadItems(this.getCurFilter());
    }, {name: "tag_workspace_load"});

    reaction(() => this.rootStore.getActiveItem, () => this.loadActiveLinks(), {name: "tag_onActive_loadLinks"});

    this.setupStorage();
  }
  protected getCurFilter = (): Query => ({
  });
  protected setupStorage() {
    // Tag links to items of the current room
    this.roomLinks = new DataContainer<ITagItem, ITagItem>(
      this.rootStore.authStore.getCurRoomID 
        ? {room_id: this.rootStore.authStore.getCurRoomID} : null, // By setting the query to null here, the container waits with loading data
      this.item_link_service,
      {
        paginate:   false,
        limited:    false,
        sortProp:   "id",
        eventFilter: (tag_item: ITagItem) => Number(tag_item.room_id) === Number(this.rootStore.authStore.getCurRoomID),
        name:       this.data_type + ".links",
      }
    );

    this.roomLinks.load();

    // Update the query when the room is changed.
    reaction(() => this.rootStore.authStore.getCurRoomID, () => {
      if(this.rootStore.authStore.getCurRoomID)
        this.roomLinks.setQuery({room_id: this.rootStore.authStore.getCurRoomID});
    });

    // Tag - Items Link event listeners
    this.item_link_service.on("created", (data: ITagItem) => {
      this.roomLinks.update(data, "created");

      if(!this.rootStore.getActiveItem || Number(data.item_id) !== this.rootStore.getActiveItem.id) return;
      if(this.activeTags.includes(Number(data.tag_id)) || this.activeTagLinkIds.includes(Number(data.id))) {
        console.error("Tag-Item link already exists", data);
        return;
      }
      runInAction(() => {
        this.activeTags.push(Number(data.tag_id));
        this.activeTagLinkIds.push(Number(data.id));
      });
    });
    this.item_link_service.on("removed", (data: ITagItem) => {
      this.roomLinks.update(data, "removed");

      if(!this.rootStore.getActiveItem || Number(data.item_id) !== this.rootStore.getActiveItem.id) return;
      if(!this.activeTags.includes(Number(data.tag_id)) || !this.activeTagLinkIds.includes(Number(data.id))) {
        console.error("Tag-Item link already exists", data);
        return;
      }
      runInAction(() => {
        this.activeTags       = this.activeTags.filter( id => id !== Number(data.tag_id));
        this.activeTagLinkIds = this.activeTagLinkIds.filter( id => id !== Number(data.id));
      })
    });
  }

  /*******************************************************************************************************************************************
   * Getter 
   ********************************************************************************************************************************************/
  getTagsNotInSpace = computedFn((space_id: number): Tag[] => 
    sortAlph(this.getItems.filter(t => !t.spaces.includes(space_id)), t => t.title?.toLowerCase())
  );
  /**
   * Return tags of current room
   */
  get getRoomTags(): Tag[] {
    const room_id = this.rootStore.getActiveItem ? this.rootStore.getActiveItem.room_id : this.rootStore.authStore.getCurRoomID;
    if(!room_id) return [];
    return sortAlph(
      Object.values(this.container).filter(t => t.spaces.includes(room_id))
    , t => t.title?.toLowerCase());
  }
  /**
   * Return tags of current project
   */
  get getProjectTags(): Tag[] {
    if(!this.rootStore.authStore.getCurProjectID) return [];
    return sortAlph(
      Object.values(this.container).filter(t => t.spaces.includes(this.rootStore.authStore.getCurProjectID))
    , t => t.title?.toLowerCase());
  }

  /**
   * @return tags linked to @item_id 
   * The item must be in the current room to ensure the corresponding links are loaded.
   */
  getItemTags = computedFn((item_id: number) => {
    return sortAlph(
      this.roomLinks.getArray
        .filter(tag_item => Number(tag_item.item_id) === Number(item_id))
        .map(tag_item => this.container[tag_item.tag_id])
    ,t => t.title?.toLowerCase());
  });

  get getActiveItemTags(): Tag[] {
    return sortAlph(
      this.activeTags.map(t => this.getItemByID(t))
    , t => t.title?.toLowerCase());
  }

  get getTagOptions(): Tag[] {
    const room_id = this.rootStore.getActiveItem ? this.rootStore.getActiveItem.room_id : this.rootStore.authStore.getCurRoomID;
    if(!room_id) return [];
    return sortAlph(
      Object.values(this.container).filter(t => t.spaces.includes(room_id) && !this.activeTags.includes(t.id))
    , t => t.title?.toLowerCase());
  }

  /*******************************************************************************************************************************************
   * Setter 
   ********************************************************************************************************************************************/

  /*******************************************************************************************************************************************
   * Actions
   ********************************************************************************************************************************************/
  /**
   * Link tag with @tag_id to active item
   */
  linkTag = (tag_id: number) => {
    if(!this.rootStore.getActiveItem || this.activeTags.includes(tag_id)) return;

    const link: ITagItem = {
      tag_id:       tag_id,
      item_id:      this.rootStore.getActiveItem.id,
      workspace_id: this.rootStore.getActiveItem.workspace_id,
      room_id:      this.rootStore.getActiveItem.room_id,
    };
    try {
      this.item_link_service.create(link);
    } catch (err) {
      console.error("Failed to link tag to item", err);
    }
  }

  /**
   * Link tag with @tag_id to active item
   */
  unlinkTag = (tag_id: number) => {
    if(!this.rootStore.getActiveItem || !this.activeTags.includes(tag_id)) return;

    try {
      this.item_link_service.remove(this.activeTagLinkIds[this.activeTags.indexOf(tag_id)]);
    } catch (err) {
      console.error("Failed to link tag to item", err);
    }
  }
}